import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BackupIcon from '@mui/icons-material/BackupOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import RestoreIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import { PageHeader } from '../../components/PageHeader';
import {
  BackupJobStatus,
  BackupKind,
  BackupOverview,
  BackupSettings,
  createBackupNow,
  DatabaseBackup,
  deleteBackup,
  downloadBackup,
  getBackupOverview,
  restoreBackup,
  saveBackupSettings,
} from '../../api/backups';

const QUERY_KEY = ['database-backups'];

const errorMessage = (err: unknown) =>
  isAxiosError(err) ? ((err.response?.data as { message?: string } | undefined)?.message ?? err.message) : String(err);

const formatBytes = (bytes: number | null | undefined) => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
};

const formatDateTime = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Colombo' })
    : '—';

const STATUS_LABEL: Record<BackupJobStatus, string> = { QUEUED: 'Queued', RUNNING: 'Running', COMPLETED: 'Completed', FAILED: 'Failed' };
const STATUS_COLOR: Record<BackupJobStatus, 'default' | 'info' | 'success' | 'error'> = {
  QUEUED: 'default',
  RUNNING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
};
const KIND_LABEL: Record<BackupKind, string> = { MANUAL: 'Manual', DAILY: 'Daily', WEEKLY: 'Weekly', PRE_RESTORE: 'Before restore' };
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const StatusChip = ({ backup }: { backup: Pick<DatabaseBackup, 'status' | 'deletedAt'> }) =>
  backup.deletedAt ? (
    <Chip size="small" variant="outlined" label="Deleted" />
  ) : (
    <Chip
      size="small"
      color={STATUS_COLOR[backup.status]}
      label={STATUS_LABEL[backup.status]}
      icon={backup.status === 'RUNNING' || backup.status === 'QUEUED' ? <CircularProgress size={12} sx={{ ml: 1 }} /> : undefined}
    />
  );

const SummaryCard = ({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ mt: 0.5, fontWeight: 600, fontSize: '1.1rem', wordBreak: 'break-word' }}>{value}</Box>
      {sub && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ wordBreak: 'break-all' }}>
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

/** Administration → Database Backups (Super Admin only; the API enforces it
 * too). Backups and restores run as background jobs; while one is active
 * the page polls every two seconds. */
export const DatabaseBackupsPage = () => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DatabaseBackup | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<DatabaseBackup | null>(null);

  const overview = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getBackupOverview,
    refetchInterval: (query) => (query.state.data?.activeJob ? 2000 : false),
  });
  const data = overview.data;
  const busy = !!data?.activeJob;

  const createMutation = useMutation({
    mutationFn: createBackupNow,
    onSuccess: () => {
      setMessage({ severity: 'success', text: 'Backup started. It runs in the background — you can keep working.' });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => setMessage({ severity: 'error', text: errorMessage(err) }),
  });

  const download = async (backup: DatabaseBackup) => {
    try {
      await downloadBackup(backup.id);
    } catch (err) {
      setMessage({ severity: 'error', text: errorMessage(err) });
    }
  };

  if (overview.isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (overview.isError || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{errorMessage(overview.error)}</Alert>
      </Box>
    );
  }

  const toolsReady = data.tools.dump.available && data.tools.restore.available && data.tools.storage.writable;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        icon={BackupIcon}
        color="#0E7490"
        title="Database Backups"
        subtitle="Back up, download and restore the CRM database."
        actions={
          <Button
            variant="contained"
            startIcon={<BackupIcon />}
            disabled={busy || createMutation.isPending || !toolsReady}
            onClick={() => createMutation.mutate()}
          >
            Create Backup Now
          </Button>
        }
      />

      {!toolsReady && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Backups can't run on this server yet:{' '}
          {[
            !data.tools.dump.available && 'the backup tool (mysqldump) is not installed',
            !data.tools.restore.available && 'the restore tool (mysql) is not installed',
            !data.tools.storage.writable && 'the backup storage folder is not writable',
          ]
            .filter(Boolean)
            .join('; ')}
          .
        </Alert>
      )}
      {data.activeJob && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }} data-testid="active-job">
          {data.activeJob.type === 'RESTORE' ? 'Restoring' : 'Backing up'} — {STATUS_LABEL[data.activeJob.status]}:{' '}
          {data.activeJob.filename}
          {data.activeJob.type === 'RESTORE' && '. The CRM may be briefly unavailable; do not change data until this finishes.'}
        </Alert>
      )}
      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 2, mb: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' } }}>
          <SummaryCard label="Last successful backup" value={formatDateTime(data.lastSuccessful?.completedAt)} sub={data.lastSuccessful?.filename} />
          <SummaryCard
            label="Latest backup status"
            value={data.latest ? <StatusChip backup={data.latest} /> : 'No backups yet'}
            sub={data.latest?.status === 'FAILED' ? data.latest.errorMessage : formatDateTime(data.latest?.createdAt)}
          />
          <SummaryCard label="Backup file size" value={formatBytes(data.lastSuccessful?.sizeBytes)} sub="Last successful backup" />
          <SummaryCard label="Total available backups" value={data.totalAvailable} sub={`${formatBytes(data.totalSizeBytes)} in total`} />
          <SummaryCard
            label="Database name"
            value={data.databaseName}
            sub={data.tools.storage.freeBytes !== null ? `${formatBytes(data.tools.storage.freeBytes)} free for backups` : undefined}
          />
      </Box>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Backup history
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Backup file</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created by</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        No backups yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {data.history.map((b) => (
                  <TableRow key={b.id} hover sx={{ opacity: b.deletedAt ? 0.55 : 1 }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{b.filename}</TableCell>
                    <TableCell>{formatDateTime(b.createdAt)}</TableCell>
                    <TableCell>{formatBytes(b.sizeBytes)}</TableCell>
                    <TableCell>{KIND_LABEL[b.kind]}</TableCell>
                    <TableCell>
                      <Tooltip title={b.errorMessage ?? (b.deletedAt ? `Deleted by ${b.deletedByName ?? 'unknown'}` : '')}>
                        <span>
                          <StatusChip backup={b} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{b.createdByName ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Download">
                        <span>
                          <IconButton size="small" aria-label={`Download ${b.filename}`} disabled={!b.available} onClick={() => void download(b)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Restore">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Restore ${b.filename}`}
                            disabled={!b.available || busy}
                            onClick={() => setRestoreTarget(b)}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Delete ${b.filename}`}
                            disabled={!!b.deletedAt || b.status === 'QUEUED' || b.status === 'RUNNING'}
                            onClick={() => setDeleteTarget(b)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ScheduleCard overview={data} onSaved={(text) => setMessage({ severity: 'success', text })} onError={(text) => setMessage({ severity: 'error', text })} />

      {data.restores.length > 0 && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Restore history
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Restored from</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell>Safety backup taken first</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.restores.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.filename}</TableCell>
                      <TableCell>{formatDateTime(r.startedAt ?? r.createdAt)}</TableCell>
                      <TableCell>
                        <Tooltip title={r.errorMessage ?? ''}>
                          <span>
                            <StatusChip backup={{ status: r.status, deletedAt: null }} />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{r.initiatedByName}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.preRestoreFilename ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
        Backup tool: {data.tools.dump.available ? `${data.tools.dump.path} ${data.tools.dump.version ?? ''}` : 'not installed'} · Backups are
        stored on the server outside the website and can only be downloaded here.
      </Typography>

      <DeleteBackupDialog
        backup={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={(filename) => {
          setDeleteTarget(null);
          setMessage({ severity: 'success', text: `${filename} was deleted.` });
          void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        }}
      />
      <RestoreBackupDialog
        backup={restoreTarget}
        onClose={() => setRestoreTarget(null)}
        onStarted={(filename) => {
          setRestoreTarget(null);
          setMessage({
            severity: 'success',
            text: `Restore of ${filename} started. A safety backup of the current database is taken first.`,
          });
          void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        }}
      />
    </Box>
  );
};

const ScheduleCard = ({
  overview,
  onSaved,
  onError,
}: {
  overview: BackupOverview;
  onSaved: (text: string) => void;
  onError: (text: string) => void;
}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BackupSettings>(overview.settings);
  useEffect(() => setForm(overview.settings), [overview.settings]);
  const save = useMutation({
    mutationFn: () => saveBackupSettings(form),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next);
      onSaved('Backup schedule saved.');
    },
    onError: (err) => onError(errorMessage(err)),
  });
  const set = <K extends keyof BackupSettings>(key: K, value: BackupSettings[K]) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600}>
          Automatic backups
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
          Times are Sri Lanka time ({overview.settings.timeZone}). Older daily and weekly backups beyond the numbers kept are removed
          automatically; manual backups, backups taken before a restore and the latest successful backup are never removed.
          {!overview.settings.schedulerRunning && ' Automatic backups are turned off on this server.'}
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, alignItems: 'center', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={<Switch checked={form.dailyEnabled} onChange={(e) => set('dailyEnabled', e.target.checked)} />}
                label="Daily backup"
              />
              <TextField
                size="small"
                type="time"
                label="At"
                value={form.dailyTime}
                onChange={(e) => set('dailyTime', e.target.value)}
                disabled={!form.dailyEnabled}
                sx={{ width: 130 }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="number"
                label="Keep latest"
                value={form.dailyRetention}
                onChange={(e) => set('dailyRetention', Number(e.target.value))}
                sx={{ width: 120 }}
                slotProps={{ htmlInput: { min: 1, max: 365 } }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={<Switch checked={form.weeklyEnabled} onChange={(e) => set('weeklyEnabled', e.target.checked)} />}
                label="Weekly backup"
              />
              <TextField
                select
                size="small"
                label="On"
                value={form.weeklyDay}
                onChange={(e) => set('weeklyDay', Number(e.target.value))}
                disabled={!form.weeklyEnabled}
                sx={{ width: 140 }}
              >
                {WEEKDAYS.map((d, i) => (
                  <MenuItem key={d} value={i}>
                    {d}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="time"
                label="At"
                value={form.weeklyTime}
                onChange={(e) => set('weeklyTime', e.target.value)}
                disabled={!form.weeklyEnabled}
                sx={{ width: 130 }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                size="small"
                type="number"
                label="Keep latest"
                value={form.weeklyRetention}
                onChange={(e) => set('weeklyRetention', Number(e.target.value))}
                sx={{ width: 120 }}
                slotProps={{ htmlInput: { min: 1, max: 104 } }}
              />
            </Stack>
        </Box>
        <Button variant="outlined" sx={{ mt: 2 }} disabled={save.isPending} onClick={() => save.mutate()}>
          Save schedule
        </Button>
      </CardContent>
    </Card>
  );
};

const DeleteBackupDialog = ({
  backup,
  onClose,
  onDeleted,
}: {
  backup: DatabaseBackup | null;
  onClose: () => void;
  onDeleted: (filename: string) => void;
}) => {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setTyped('');
    setError(null);
  }, [backup]);
  const mutation = useMutation({
    mutationFn: () => deleteBackup(backup!.id, typed),
    onSuccess: () => onDeleted(backup!.filename),
    onError: (err) => setError(errorMessage(err)),
  });
  return (
    <Dialog open={!!backup} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete backup?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The file <b>{backup?.filename}</b> will be permanently removed from the server. Its record stays in the history.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Type the file name to confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" disabled={typed !== backup?.filename || mutation.isPending} onClick={() => mutation.mutate()}>
          Delete backup
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RestoreBackupDialog = ({
  backup,
  onClose,
  onStarted,
}: {
  backup: DatabaseBackup | null;
  onClose: () => void;
  onStarted: (filename: string) => void;
}) => {
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setTyped('');
    setPassword('');
    setAcknowledged(false);
    setError(null);
  }, [backup]);
  const mutation = useMutation({
    mutationFn: () => restoreBackup(backup!.id, { confirmFilename: typed, password, acknowledge: true }),
    onSuccess: () => {
      setPassword('');
      onStarted(backup!.filename);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const ready = typed === backup?.filename && password.length > 0 && acknowledged;
  return (
    <Dialog open={!!backup} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Restore database from backup?</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Restoring <b>replaces the current contents of the whole database</b> with this backup from{' '}
          {formatDateTime(backup?.completedAt)}. Everything recorded since then — students, payments, documents, users — will be
          replaced. A safety backup of the current database is taken first; if it fails, nothing is restored.
        </Alert>
        <Stack spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="Type the backup file name to confirm"
            helperText={backup?.filename}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
          <TextField
            fullWidth
            size="small"
            type="password"
            label="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <FormControlLabel
            control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
            label="I understand the current database contents will be replaced."
          />
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" disabled={!ready || mutation.isPending} onClick={() => mutation.mutate()}>
          Restore database
        </Button>
      </DialogActions>
    </Dialog>
  );
};
